import clsx from "clsx";

import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { typedObjectEntries } from "common/helpers/utils";

import { useLead } from "../datahooks";

interface LeadSourceProps {
  leadId: string;
  variant: "tagMd" | "tagSm" | "plain";
}

export default function LeadSource({ leadId, variant }: LeadSourceProps) {
  const { lead, isLoadingLead } = useLead({ profileId: leadId });

  if (isLoadingLead || !lead) {
    let heightClassName: string;
    if (variant === "tagMd") {
      heightClassName = "h-6";
    } else if (variant === "tagSm") {
      heightClassName = "h-5";
    } else {
      heightClassName = "h-4";
    }
    return <Skeleton className={clsx("w-24", heightClassName)} />;
  }

  const { origins } = lead;

  if (!origins.length) {
    return;
  }

  const content = origins.length > 1 ? "Multiple sources" : origins[0].name;
  const component =
    variant === "plain" ? (
      <span className="shrink truncate max-w-[40ch] line-clamp-1 text-md-semibold">
        {content}
      </span>
    ) : (
      <Tag size={variant === "tagMd" ? "md" : "sm"} variant="tertiary">
        {content}
      </Tag>
    );

  const groupedOrigins = origins.reduce<Record<string, number>>(
    (accumulator, { name }) => {
      if (accumulator[name]) {
        accumulator[name] += 1;
      } else {
        accumulator[name] = 1;
      }
      return accumulator;
    },
    {},
  );

  return origins.length > 1 ? (
    <Tooltip>
      <TooltipTrigger asChild>{component}</TooltipTrigger>

      <TooltipContent className="w-fit max-w-64">
        {typedObjectEntries(groupedOrigins)
          .map(([name, count]) => (count > 1 ? `${name} x${count}` : name))
          .join(", ")}
      </TooltipContent>
    </Tooltip>
  ) : (
    component
  );
}
